import { useEffect, useState } from 'react';
import { useSupplierAuth } from '@/hooks/useSupplierAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import confetti from 'canvas-confetti';

interface SupplierProtectedRouteProps {
  children: React.ReactNode;
}

const SupplierProtectedRoute = ({ children }: SupplierProtectedRouteProps) => {
  const { user, userRole, isSupplier, isApproved, isLoading, redirectToLogin } = useSupplierAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showGCalSuccess, setShowGCalSuccess] = useState(false);

  const isGCalConnected = searchParams.get('google_calendar') === 'connected';

  // If this page loaded in a popup from OAuth, notify opener and close
  useEffect(() => {
    if (isGCalConnected && window.opener) {
      // We're in a popup — notify the parent window
      try {
        window.opener.postMessage({ type: 'google_calendar_connected' }, '*');
      } catch (e) {
        console.error('[GCal Popup] Failed to postMessage to opener', e);
      }
      // Show brief success then close
      setShowGCalSuccess(true);
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      setTimeout(() => {
        window.close();
      }, 2000);
      return;
    }
  }, [isGCalConnected]);

  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        redirectToLogin();
        return;
      }

      if (userRole === null) {
        return;
      }

      if (!isSupplier) {
        // If returning from Google Calendar OAuth without session (top-level fallback)
        if (isGCalConnected && !window.opener) {
          setShowGCalSuccess(true);
          confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
          setTimeout(() => confetti({ particleCount: 100, spread: 120, origin: { y: 0.5 } }), 300);
          const returnUrl = localStorage.getItem('gcal_return_url');
          localStorage.removeItem('gcal_return_url');
          setTimeout(() => {
            window.location.href = returnUrl || '/fornecedor/painel/eventos';
          }, 3000);
          return;
        }

        // If user is admin (or any other role), don't bounce to /login — that
        // creates a redirect loop because LoginPage may try to honor a stale
        // postLoginRedirect to /fornecedor/... Send them to their own area.
        if (userRole === 'admin') {
          localStorage.removeItem('postLoginRedirect');
          navigate('/admin', { replace: true });
          return;
        }

        redirectToLogin();
      } else if (isSupplier && isApproved === false) {
        navigate('/?message=awaiting-approval', { replace: true });
      }
    }
  }, [isLoading, user, userRole, isSupplier, isApproved, redirectToLogin, isGCalConnected, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  if (showGCalSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-8 space-y-6">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold">Google Agenda Conectado! 🎉</h1>
          <p className="text-muted-foreground">
            Sua conta do Google Agenda foi conectada com sucesso. Agora os seus eventos estão sendo sincronizados no Google e na AmeUp.
          </p>
          <p className="text-sm text-muted-foreground">Redirecionando...</p>
        </div>
      </div>
    );
  }

  if (!isSupplier || isApproved === false) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg">Redirecionando...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default SupplierProtectedRoute;